import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle, Edit3, FileText, XCircle } from "react-feather";
import toast from "react-hot-toast";
import SectionCard from "../../../components/common/SectionCard";
import ReasonPrompt from "../../../components/common/ReasonPrompt";
import SurveyHeader from "./components/SurveyHeader";
import SurveyMaterialsCard from "./components/SurveyMaterialsCard";
import SurveyAttachments from "./components/SurveyAttachments";
import SurveyEditModal from "./components/SurveyEditModal";
import SurveyQuotationAction from "./components/SurveyQuotationAction";
import { useCancelSurveyMutation, useCompleteSurveyMutation, useGetSurveyQuery } from "./surveysApi";
import { paths } from "../../../routes/paths";

const TextBlock = ({ value }) =>
  value ? <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>{value}</p> : <span className="text-muted small">Nothing recorded.</span>;

/**
 * One site survey: header (party, site, contact, GPS, trail), requirements and
 * remarks, materials and attachments. Every action button is driven by the
 * detail's own canEdit / canComplete / canCancel / canConvert flags.
 */
const SurveyDetailPage = () => {
  const { recordGuid } = useParams();
  const { data: survey, isLoading, isError, error } = useGetSurveyQuery(recordGuid);
  const [completeSurvey, { isLoading: isCompleting }] = useCompleteSurveyMutation();
  const [cancelSurvey] = useCancelSurveyMutation();
  const [showEdit, setShowEdit] = useState(false);
  const [showCancel, setShowCancel] = useState(false);

  const back = (
    <Link to={paths.technicalSurveys} className="d-inline-flex align-items-center mb-2">
      <ArrowLeft size={16} className="me-1" /> Back to Site Surveys
    </Link>
  );

  if (isLoading) {
    return (
      <div className="page-wrapper"><div className="content">{back}<p className="text-muted">Loading…</p></div></div>
    );
  }
  if (isError || !survey) {
    return (
      <div className="page-wrapper">
        <div className="content">
          {back}
          <div className="alert alert-danger">{error?.message || "Could not load this survey."}</div>
        </div>
      </div>
    );
  }

  const handleComplete = async () => {
    if (!window.confirm("Mark this survey as completed? It goes to the quotation team and you can no longer edit it.")) return;
    try {
      await completeSurvey(recordGuid).unwrap();
      toast.success("Survey completed");
    } catch (e) {
      toast.error(e?.message || "Could not complete the survey");
    }
  };

  const handleCancel = async (reason) => {
    const trimmed = (reason || "").trim();
    if (!trimmed) return toast.error("A reason is required");
    if (trimmed.length > 300) return toast.error("Keep the reason under 300 characters");
    try {
      await cancelSurvey({ recordGuid, reason: trimmed }).unwrap();
      toast.success("Survey cancelled");
      setShowCancel(false);
    } catch (e) {
      toast.error(e?.message || "Could not cancel the survey");
    }
  };

  const hasActions = survey.canEdit || survey.canComplete || survey.canCancel || survey.canConvert || survey.quotationRecordGuid;

  return (
    <div className="page-wrapper">
      <div className="content">
        {back}
        <SurveyHeader survey={survey} />

        {hasActions && (
          <div className="d-flex flex-wrap gap-2 mb-3">
            <SurveyQuotationAction survey={survey} />
            {survey.canEdit && (
              <button type="button" className="btn btn-primary d-inline-flex align-items-center" onClick={() => setShowEdit(true)}>
                <Edit3 size={15} className="me-1" /> Edit Survey
              </button>
            )}
            {survey.canComplete && (
              <button type="button" className="btn btn-outline-success d-inline-flex align-items-center" onClick={handleComplete} disabled={isCompleting}>
                <CheckCircle size={15} className="me-1" /> {isCompleting ? "Completing..." : "Mark Completed"}
              </button>
            )}
            {survey.canCancel && !showCancel && (
              <button type="button" className="btn btn-outline-danger d-inline-flex align-items-center" onClick={() => setShowCancel(true)}>
                <XCircle size={15} className="me-1" /> Cancel Survey
              </button>
            )}
          </div>
        )}

        {showCancel && (
          <ReasonPrompt
            label="Why is this survey being cancelled? (required)"
            confirmLabel="Cancel Survey"
            onConfirm={handleCancel}
            onCancel={() => setShowCancel(false)}
          />
        )}

        <div className="row">
          <div className="col-lg-7">
            <SurveyMaterialsCard lines={survey.lines} groupLines={survey.groupLines} />
          </div>
          <div className="col-lg-5">
            <SectionCard icon={FileText} title="Requirements">
              <TextBlock value={survey.requirements} />
            </SectionCard>
            <SectionCard icon={Edit3} title="Remarks">
              <TextBlock value={survey.remarks} />
            </SectionCard>
          </div>
        </div>

        <SurveyAttachments recordGuid={recordGuid} attachments={survey.attachments || []} canEdit={survey.canEdit} />
      </div>

      {survey.canEdit && <SurveyEditModal show={showEdit} survey={survey} onClose={() => setShowEdit(false)} />}
    </div>
  );
};

export default SurveyDetailPage;
